/// <reference types="@angular/localize" />

import { enableProdMode } from '@angular/core';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import * as Sentry from '@sentry/angular';
import {
  cleanupLegacyRootServiceWorkerState,
  shouldFilterWebsocketNotReadySentryEvent,
  shouldFilterWebsocketNotReadySentryLog,
} from 'shared/core';
import * as Video from 'twilio-video';
import { platformBrowser } from '@angular/platform-browser';
import { buildDate, commitHash } from './environments/version';

const normalizedCommitHash = String(commitHash);
const sentryRelease =
  normalizedCommitHash !== '<local-build>' && normalizedCommitHash !== 'local'
    ? `aidar-web-${environment.environment}-${normalizedCommitHash}`
    : `aidar-web-${environment.environment}-local-${buildDate
        .replace(/[-:.TZ]/g, '')
        .slice(0, 14)}`;

if (!environment.production) {
  const { Logger } = Video;
  const logger = Logger.getLogger('twilio-video');
  const originalFactory = logger.methodFactory;
  logger.methodFactory = function (methodName, logLevel, loggerName) {
    const method = originalFactory(methodName, logLevel, loggerName);
    return function (datetime, logLevel, component, message, data) {
      const prefix = '[Twilio - Helper Client]';
      method(prefix, datetime, logLevel, component, message, data);
    };
  };

  switch (environment.video.logLevel) {
    case 'trace':
      logger.setLevel('trace');
      break;
    case 'info':
      logger.setLevel('info');
      break;
    case 'warn':
      logger.setLevel('warn');
      break;
    case 'error':
      logger.setLevel('error');
      break;
    case 'silent':
      logger.setLevel('silent');
      break;
    default:
      logger.setLevel('debug');
      break;
  }
}

if (environment.sentryDsn && environment.useSentry) {
  Sentry.init({
    dsn: environment.sentryDsn,
    release: sentryRelease,
    enableLogs: true,
    beforeSend: (event) => {
      if (shouldFilterWebsocketNotReadySentryEvent(event)) {
        return null;
      }

      return event;
    },
    beforeSendLog: (log) => {
      if (shouldFilterWebsocketNotReadySentryLog(log)) {
        return null;
      }

      if (environment.production && ['trace', 'debug'].includes(log.level)) {
        return null;
      }
      return log;
    },
    environment: environment.environment,
    attachStacktrace: true,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.browserTracingIntegration(),
      Sentry.spanStreamingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],
    traceLifecycle: 'stream',
    tracesSampler: ({ name, inheritOrSampleWith }) =>
      name === 'video.appointment.diagnostics'
        ? 1.0
        : inheritOrSampleWith(environment.production ? 0.25 : 1.0),
    tracePropagationTargets: [environment.apiPath, environment.storagePath],
    replaysSessionSampleRate: environment.production ? 0.25 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (environment.production) {
  enableProdMode();
}

cleanupLegacyRootServiceWorkerState()
  .then(() => platformBrowser().bootstrapModule(AppModule))
  .catch((err) => {
    Sentry.captureException(err);
    Sentry.logger.error('Failed to bootstrap helper client', { err });
  });
